/* eslint-disable prefer-destructuring */
/* eslint-disable default-case */
import React from 'react';
import { styled, ThemeProvider } from '@mui/material/styles';
import ReCAPTCHA from 'react-google-recaptcha';
import axios, { API } from 'lib/config/axios';
import getConfig from 'next/config';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import LightTextField from 'views/common/components/Logical/Layout/LayoutTextField';
import NavigatorLink from 'views/common/components/Logical/NavigatorLink';
import useErrorsHandler from 'views/rebranded-module/common/hooks/useErrorsHandler';
import { useDispatch, useSelector } from 'react-redux';
import { setLoading, setSnackbar } from 'redux/features/ui-slice';
import { useTranslation } from 'next-i18next';
import PATHS from 'lib/navigation/paths';
import geGAFromCategory from 'lib/utils/geGAFromCategory';
import { CountriesAutocomplete } from 'views/common/components/UI/CountriesAutocomplete';
import erTheme from 'lib/config/theme';
import { InternationalPhone } from 'views/common/components/Logical/InternationalPhone';

const PREFIX = 'ContactForm';

const classes = {
  root: `${PREFIX}-root`,
  rootNoPadding: `${PREFIX}-rootNoPadding`,
  form: `${PREFIX}-form`,
  heading: `${PREFIX}-heading`,
  flexContainer: `${PREFIX}-flexContainer`,
  recaptch: `${PREFIX}-recaptch`,
  checkbox: `${PREFIX}-checkbox`,
  policyLink: `${PREFIX}-policyLink`,
  send: `${PREFIX}-send`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.root}`]: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.secondary.main,
    padding: theme.spacing(2, 4, 5, 4),
    marginTop: theme.spacing(2),
  },

  [`& .${classes.rootNoPadding}`]: {
    padding: theme.spacing(2, 0, 5),
  },

  [`& .${classes.form}`]: {
    paddingBottom: theme.spacing(2),
    '& > *': {
      margin: theme.spacing(1.5, 0),
    },
  },

  [`& .${classes.heading}`]: {
    marginBottom: '0 !important',
    fontSize: theme.typography.pxToRem(18),
    paddingBottom: theme.spacing(2),
  },

  [`& .${classes.flexContainer}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    '& > *': {
      flexBasis: '49%',
      maxWidth: '49%',
    },
  },

  [`& .${classes.recaptch}`]: {
    display: 'flex',
    justifyContent: 'center',
  },

  [`& .${classes.checkbox}`]: {
    color: 'rgba(0, 0, 0, 0.54)',
  },

  [`& .${classes.policyLink}`]: {
    fontWeight: 700,
    color: theme.palette.common.black,
    textDecoration: 'underline',
  },

  [`& .${classes.send}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.black,
  },
}));

const initialState = {
  category_id: '',
  lead_type_id: '',
  reservation_number: '',
  comments: '',
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  'g-recaptcha-response': '',
  referring_page: null,
  user_id: null,
  privacyPolicy: false,
};
const {
  publicRuntimeConfig: { envVars },
} = getConfig();

const ContactForm = ({
  contactInfo,
  leadCategories,
  leadTypes,
  onClose,
  userId,
  leadCategoryId,
  er,
  isContactUsPage,
}) => {
  const dispatch = useDispatch();
  const { appState, agentLocationInfo } = useSelector(
    (state) => state.globalData,
  );
  const { t } = useTranslation();
  const recaptchaRef = React.useRef();
  const handleErrorResponse = useErrorsHandler();
  const [errorMsgs, setErrorMsgs] = React.useState({});

  const [value, setValue] = React.useState({
    ...initialState,
    category_id: leadCategoryId || '',
    country_code: agentLocationInfo?.code,
  });
  React.useEffect(() => {
    // Populate returned data
    setValue({
      ...value,
      ...contactInfo,
      lead_type_id:
        contactInfo?.lead_type_id || (leadTypes && leadTypes[1]?.id) || '',
    });
  }, [contactInfo]);

  const handleInputChange = (e) => {
    setValue((prevData) => ({
      ...prevData,
      [e.target.name]: e.target.value,
    }));
  };
  const sendGoogleAnalyticsEvent = (gaInfo) => {
    const { eventName, eventLabel } = gaInfo;

    window.gtag('event', eventName, {
      event_category: 'Lead',
      event_action: 'lead_capture',
      event_label: eventLabel,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(setLoading(true));
    if (!value.privacyPolicy) {
      dispatch(
        setSnackbar({
          open: true,
          severity: 'error',
          message: er
            ? t('common_layout_v2:er_read_terms_and_conditions')
            : t('common_layout_v2:es_read_terms_and_conditions'),
        }),
      );
      dispatch(setLoading(false));
      return;
    }
    try {
      const { 'g-recaptcha-response': recaptchaResponse, ...rest } = value;
      const { data } = await axios.post(API.contactUs, {
        authenticity_token: appState.authenticity_token,
        'g-recaptcha-response': recaptchaResponse,
        contact_info: {
          ...rest,
          referring_page: window.location.href,
          user_id: userId,
          phone: rest.full_phone || rest.phone,
        },
      });
      sendGoogleAnalyticsEvent(geGAFromCategory(rest.category_id));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      dispatch(
        setSnackbar({
          open: true,
          severity: 'success',
          message: t('common_layout_v2:contact_us_help.success_msg', {
            reference_number: data.reference_number,
          }),
          duration: 10000,
        }),
      );
      setValue(initialState); // Reset dialog
      dispatch(setLoading(false));
      onClose?.();
    } catch (error) {
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      let message = '';
      let isExpected = false;
      if (error.response?.status && error.response.data) {
        const errorData = error.response.data;
        switch (error.response.status) {
          case 406:
            if (errorData.errors) {
              setErrorMsgs({
                ...errorData.errors.contact_info,
              });
              if (errorData.errors.contact_info.base) {
                message = errorData.errors.contact_info.base[0];
              } else message = null;
              isExpected = true;
            }
            dispatch(setLoading(false));
            break;
          case 403:
            message = errorData.message;
            isExpected = true;
            dispatch(setLoading(false));
            break;
        }
      }
      if (message) {
        dispatch(
          setSnackbar({
            open: true,
            severity: 'error',
            message,
          }),
        );
      }
      if (!isExpected) {
        handleErrorResponse(error);
      }
      dispatch(setLoading(false));
    }
  };

  const onRecaptchChange = (token) => {
    setValue({
      ...value,
      'g-recaptcha-response': token,
    });
  };

  return (
    <Root
      className={`${classes.root} ${isContactUsPage && classes.rootNoPadding}`}>
      <form onSubmit={handleSubmit} className={classes.form} noValidate>
        <Typography variant="h5" className={classes.heading}>
          {t('common_layout_v2:contact_us_help.header')}
        </Typography>
        <LightTextField
          error={Boolean(errorMsgs.category_id)}
          color="secondary"
          label={t('common_layout_v2:contact_us_help.product')}
          variant="outlined"
          fullWidth
          select
          name="category_id"
          value={
            (value.category_id && value.category_id)
            || leadCategories?.[0].id
            || ''
          }
          onChange={handleInputChange}
          helperText={errorMsgs.category_id}>
          {leadCategories ? (
            leadCategories.map((option) => (
              <MenuItem key={`product ${option.id}`} value={option.id}>
                {option.name}
              </MenuItem>
            ))
          ) : (
            <MenuItem key={`product ${0}`} value="" />
          )}
        </LightTextField>
        <LightTextField
          error={Boolean(errorMsgs.lead_type_id)}
          color="secondary"
          label={t('common_layout_v2:contact_us_help.reason')}
          variant="outlined"
          fullWidth
          select
          name="lead_type_id"
          value={value.lead_type_id}
          onChange={handleInputChange}
          helperText={errorMsgs.lead_type_id}>
          {leadTypes ? (
            leadTypes.map((option) => (
              <MenuItem key={`reason ${option.id}`} value={option.id}>
                {option.name}
              </MenuItem>
            ))
          ) : (
            <MenuItem key={`reason ${0}`} value="" />
          )}
        </LightTextField>
        <LightTextField
          error={Boolean(errorMsgs.reservation_number)}
          color="secondary"
          label={t('common_layout_v2:contact_us_help.reservation_number')}
          variant="outlined"
          fullWidth
          type="text"
          name="reservation_number"
          value={value.reservation_number}
          onChange={handleInputChange}
          helperText={errorMsgs.reservation_number}
          autoComplete="one-time-code"
        />
        <LightTextField
          error={Boolean(errorMsgs.comments)}
          color="secondary"
          label={t('common_layout_v2:contact_us_help.comments')}
          variant="outlined"
          multiline
          rows={3}
          fullWidth
          name="comments"
          value={value.comments}
          onChange={handleInputChange}
          helperText={errorMsgs.comments}
        />
        <Typography variant="h5" className={classes.heading}>
          {t('common_layout_v2:contact_us_help.ur_details')}
        </Typography>
        <div className={classes.flexContainer}>
          <LightTextField
            required
            error={Boolean(errorMsgs.first_name)}
            color="secondary"
            label={t('common_layout_v2:contact_us_help.first_name')}
            variant="outlined"
            name="first_name"
            type="text"
            value={value.first_name}
            onChange={handleInputChange}
            helperText={errorMsgs.first_name}
            autoComplete="name"
            disabled={Boolean(contactInfo?.first_name)}
          />
          <LightTextField
            required
            error={Boolean(errorMsgs.last_name)}
            color="secondary"
            label={t('common_layout_v2:contact_us_help.last_name')}
            variant="outlined"
            name="last_name"
            type="text"
            autoComplete="name"
            value={value.last_name}
            onChange={handleInputChange}
            helperText={errorMsgs.last_name}
            disabled={Boolean(contactInfo?.last_name)}
          />
        </div>
        <LightTextField
          required
          error={Boolean(errorMsgs.email)}
          color="secondary"
          label={t('common_layout_v2:contact_us_help.email')}
          type="email"
          variant="outlined"
          fullWidth
          name="email"
          value={value.email}
          onChange={handleInputChange}
          helperText={errorMsgs.email}
          disabled={Boolean(contactInfo?.email)}
        />
        <InternationalPhone
          required
          error={Boolean(errorMsgs.phone)}
          color="secondary"
          label={t('common_layout_v2:contact_us_help.phone')}
          type="tel"
          variant="outlined"
          fullWidth
          name="phone"
          value={value.phone}
          handleChange={(val) => {
            handleInputChange({
              target: {
                value: val?.value,
                name: 'phone',
              },
            });
            handleInputChange({
              target: {
                value: val?.number,
                name: 'full_phone',
              },
            });
          }}
          helperText={errorMsgs.phone}
          skipValidation
          // disabled={Boolean(contactInfo && contactInfo.phone)}
        />
        <ThemeProvider theme={erTheme}>
          <CountriesAutocomplete
            label={t('common_layout_v2:select_country')}
            value={value.country_code}
            name="country_code"
            onChange={(_, code) => {
              handleInputChange({
                target: {
                  name: 'country_code',
                  value: code,
                },
              });
            }}
          />
        </ThemeProvider>

        <Typography variant="body2">
          {t('common_layout_v2:contact_us_help.captcha')}
        </Typography>
        {envVars.RECAPTCHA_SITE_KEY && (
          <div className={classes.recaptch}>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={envVars.RECAPTCHA_SITE_KEY}
              onChange={onRecaptchChange}
            />
          </div>
        )}
        <FormControlLabel
          value="end"
          control={(
            <Checkbox
              color="primary"
              classes={{ root: classes.checkbox }}
              name="privacyPolicy"
              checked={value.privacyPolicy}
              onChange={(e) => setValue({
                ...value,
                privacyPolicy: e.target.checked,
              })}
            />
          )}
          label={(
            <Typography>
              {t('common_layout_v2:contact_us_help.read_agreed')}
              &nbsp;
              <NavigatorLink
                href={PATHS.privacyPolicy()}
                color="primary"
                className={classes.policyLink}
                target="_blank">
                {t('common_layout_v2:er_privacy_policy')}
              </NavigatorLink>
            </Typography>
          )}
          labelPlacement="end"
        />
        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          className={classes.send}>
          {t('common_layout_v2:contact_us_help.send')}
        </Button>
      </form>
    </Root>
  );
};

export default ContactForm;
